import { useState, useEffect, useMemo } from "react";
import { i18n } from "../translate/i18n";

import { createFile } from '../model/services/FilesService'
import { useUserCtx } from "../model/useUsersCtx";
import { useAuth } from "../templates/AuthGate";

import { COLUMNS_IDENTIFIERS, COLUMNS_LABELS, USER_ROLES } from "../components/components.constants";
import { useStoresData } from "../model/useStores";
import { ROUTES_APPLICATION_PATH } from "../routes.constants";

export const SETTINGS_ROUTES = Object.freeze({
    BASE_PATH: ROUTES_APPLICATION_PATH.SETTINGS.BASE_URL,
    ACCOUNT: {
        ROUTE: "conta",
        PATH: "conta"
    },
    USER_MANAGEMENT: {
        ROUTE: 'usuarios',
        PATH: 'usuarios'
    }
});

export const useSeetingsViewModel = ({
    shouldFetchUserCtx,
    shouldFetchUsersInformation,
    shouldFetchStores
}) => {

    const { t } = i18n;

    const { 
        resetPassword, 
        usersInformation, 
        isLoadingUsersInformation, 
        userCtx,
        registerUser,
        isRegisteringUser,
        registerUserError,
        onSuccessRegister,
        deactivateUserInTenantApp,
        isDeactivatingUser,
        refetchUserCtx
    } = useUserCtx({
        shouldFetchUserCtx: shouldFetchUserCtx,
        shouldFetchUsersInformation: shouldFetchUsersInformation,
        shouldFetchStores: shouldFetchStores
    });

    const { storeNameRegionAndId } = useStoresData({ shouldFetchStores: shouldFetchStores });

    const userInitialInformation = useAuth();

    const [uploadedFile, setUploadedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [showSavedPhotoModal, setShowSavedPhotoModal] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [resultMessage, setResultMessage] = useState("");
    const [passwordValidations, setPasswordValidations] = useState({
        hasCapitalLetter: false,
        hasLowercaseLetter: false,
        hasNumber: false,
        hasSpecialCharacter: false,
        hasMinimumLength: false,
    });
    const [selectedStores, setSelectedStores] = useState([]);

    const usersTenant = userInitialInformation?.tenant?.name || "aidron";

    const tabs = [
        { name: t("myAccount"), route: SETTINGS_ROUTES.ACCOUNT.ROUTE },
        (userCtx?.data?.role === USER_ROLES.TENANT_ADMIN || userCtx?.data?.role === USER_ROLES.ADMIN) && 
        { name: t("usersManagement"), route: SETTINGS_ROUTES.USER_MANAGEMENT.ROUTE }
    ].filter(Boolean);

    const columnsAccountLastRegistered = [
        {
            accessorKey: COLUMNS_IDENTIFIERS.USERNAME,
            id: COLUMNS_IDENTIFIERS.USERNAME,
            header: COLUMNS_LABELS.USERNAME_MANAGEMENT,
            enableSorting: true,
            cell: (info) => {
                const username = info.getValue();
                const fallbackTenantName = info.row.original.name;
                return username || fallbackTenantName || null;
            },
        },
        {
            accessorKey: COLUMNS_IDENTIFIERS.DEPARTMENT,
            id: COLUMNS_IDENTIFIERS.DEPARTMENT,
            header: COLUMNS_LABELS.DEPARTMENT,
                cell: (info) => info.getValue() ?? null,
            enableSorting: true,
        },
        {
            accessorKey: COLUMNS_IDENTIFIERS.CREATED_AT,
            id: COLUMNS_IDENTIFIERS.CREATED_AT,
            header: COLUMNS_LABELS.REGISTERED_ON,
            enableSorting: true,
            cell: (info) => {
                const rawValue = info.getValue();
                if (!rawValue) return null;
                const date = new Date(rawValue);

                return date.toLocaleString("pt-BR", {
                    timeZone: "America/Sao_Paulo",
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                });
            },
            sortingFn: "datetime",
        },
    ];

    const columnsUserManagement = useMemo(() => [
    {
        accessorKey: COLUMNS_IDENTIFIERS.USERNAME,
        id: COLUMNS_IDENTIFIERS.USERNAME,
        header: COLUMNS_LABELS.USERNAME_MANAGEMENT,
        enableSorting: true,
        cell: (info) => {
        const username = info.getValue();
        const fallbackTenantName = info.row.original.name;
        return username || fallbackTenantName || null;
        },
    },
    {
        accessorKey: COLUMNS_IDENTIFIERS.EMAIL,
        id: COLUMNS_IDENTIFIERS.EMAIL,
        header: COLUMNS_LABELS.EMAIL,
        cell: (info) => info.getValue() ?? null,
        enableSorting: true,
    },
    {
        accessorKey: COLUMNS_IDENTIFIERS.DEPARTMENT,
        id: COLUMNS_IDENTIFIERS.DEPARTMENT,
        header: COLUMNS_LABELS.DEPARTMENT,
        cell: (info) => info.getValue() ?? null,
        enableSorting: true,
    },
    {
        accessorKey: COLUMNS_IDENTIFIERS.STORES,
        id: COLUMNS_IDENTIFIERS.STORES,
        header: COLUMNS_LABELS.STORES,
        cell: (info) => info.getValue() ?? null,
        enableSorting: true,
    },
    ], []);

    const roleOptions = [
        USER_ROLES.SHOPKEEPER,
        USER_ROLES.REGIONAL_GAS_STATION
    ];

    const usersOptions = () => {
        if (usersInformation) {
            return (
            usersInformation?.users
                ?.filter((user) => user.role?.toUpperCase() !== USER_ROLES.ADMIN)
                .map((user) => ({
                    username: user.username,
                    userId: user.userId,
                    role: user.role,
                    stores: user.stores
                })) || []
            );
        }
        return [];
    };

    const storesOptions = () => {
        const allStores = storeNameRegionAndId?.data?.allStores || [];
        
        if (storeNameRegionAndId?.isFetched && Array.isArray(allStores)) {
            return allStores.map(store => ({
                storeName: store.level2,
                storeRegion: store.level1?.replace(/\s+\d+$/, '').trim(),
                storeId: store.storeId
            }));
        }

        return [];
    };

    const groupedStores = storesOptions()?.reduce((groups, store) => {
        const region = store.storeRegion || "N/A";
        if (!groups[region]) groups[region] = [];
        groups[region].push(store);
        return groups;
    }, {} );

    const handleFileChange = (event) => {

        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
        
        if (file.size > 5 * 1024 * 1024) {
            setErrorMessage("O arquivo deve ser menor que 5MB");
            return;
        }
        
        if (!file.type.startsWith('image/')) {
            setErrorMessage("Por favor, selecione um arquivo de imagem válido");
            return;
        }
        
            setErrorMessage("");
            setUploadedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleSave = async () => {
        if (!uploadedFile) return;
        
        setIsLoading(true);
        setErrorMessage("");
        
        try {
            const response = await createFile(uploadedFile);
        
            if (!response || !response.success) throw new Error(response?.message || 'Erro ao enviar imagem');
        
            setShowSavedPhotoModal(true);
        } catch (error) {
            console.error("Erro ao enviar imagem:", error.message);
            setErrorMessage("Falha ao salvar a imagem. Por favor, tente novamente.");
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleSavePassword = async () => {
        if (!newPassword) return;

        setIsLoading(true);

        try {
            await resetPassword(newPassword);

            setNewPassword("");
            setResultMessage(t("newPasswordSaved"));

        } catch (error) {
            setResultMessage(t("newPasswordNotSaved"));

        } finally {
            setIsLoading(false);
        }
    };

    const validatePassword = (password) => {
        setPasswordValidations({
            hasCapitalLetter: /[A-Z]/.test(password),
            hasLowercaseLetter: /[a-z]/.test(password),
            hasNumber: /[0-9]/.test(password),
            hasSpecialCharacter: /[^A-Za-z0-9]/.test(password),
            hasMinimumLength: password.length >= 6,
        });
    };

    const updateNewPassword = (password) => {
        setNewPassword(password);
        validatePassword(password);
    };

    const getHierarchyNames = (userInitialInformation) => {
        const hierarchyNames = { ...userInitialInformation.tenant };

        for (let key in hierarchyNames) {
            if (!key.includes("level")) delete hierarchyNames[key];
        }

        return {
            levels: Object.keys(hierarchyNames).sort((a, b) => a.localeCompare(b)),
            labels: hierarchyNames,
        };
    };

    const hierarchyNames = getHierarchyNames(userInitialInformation);

    const handleStoresSelection = (storeId) => {
        setSelectedStores((prev) =>
            prev.includes(storeId)
            ? prev.filter((id) => id !== storeId)
            : [...prev, storeId]
        );
    };    

    useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);

    return {
        actions: {
            handleFileChange,
            handleSave,
            setShowSavedPhotoModal,
            handleSavePassword,
            refetchUserCtx,
            setResultMessage,
            setNewPassword: updateNewPassword,
            handleStoresSelection,
            registerUser,
            deactivateUserInTenantApp
        },
        ctx: {
            tabs,
            hierarchyNames: hierarchyNames,
            userRole: userCtx?.data?.role,
            userId: userCtx?.data?.userId,
            uploadedFile,
            storesByRegion: groupedStores,
            previewUrl,
            columns: [columnsAccountLastRegistered, columnsUserManagement],
            newPassword,
            usersInformation: usersInformation,
            selectOptions: [roleOptions, usersOptions(), storesOptions()],
            usersTenant
        },
        uiState: {
            errorMessage,
            isLoading: isLoading || isLoadingUsersInformation,
            resultMessage,
            passValidationRules: passwordValidations,
            showSavedPhotoModal,
            selectedStores,
            isDeactivatingUser,
            isRegisteringUser,
            onSuccessRegister,
            registerUserError
        }
    }
};